// Version grouping for the gallery. Several model files can describe the *same
// object* in different styles — "avenue", "avenue-v2", "avenue-v3" are one
// avenue rendered three ways. The library keeps them as independent models (each
// is its own file, id and MCP resource); the gallery merges them into a single
// tile so the grid shows one entry per object, with its versions reachable on
// hover and in the detail view. These are pure helpers (no three.js, no DOM) so
// they can be unit-tested and shared between the grid and the detail viewer.

import { batchOf } from "../batches";

/** A trailing version suffix on an id: "-v2", "-v3", … (the base is unsuffixed). */
const VERSION_SUFFIX = /-v(\d+)$/;
/** A trailing "(v2)" on a display name. */
const VERSION_NAME_SUFFIX = /\s*\(v\d+\)\s*$/i;

/** Strip the version suffix from an id — the grouping key. "avenue-v2" → "avenue". */
export function versionStem(id: string): string {
  return id.replace(VERSION_SUFFIX, "");
}

/** The version ordinal encoded in an id: "-v2" → 2, "-v3" → 3, no suffix → 1. */
export function versionOrdinal(id: string): number {
  const m = VERSION_SUFFIX.exec(id);
  return m ? Number(m[1]) : 1;
}

/** Strip a trailing "(v2)" from a display name. "Avenue (v2)" → "Avenue". */
export function baseName(name: string): string {
  return name.replace(VERSION_NAME_SUFFIX, "").trim();
}

/**
 * The short label a version carries within its group's switcher: the name of
 * the batch (production run) it was generated in — "V2 Redesign", "Future
 * Library", … — which is the provenance the version actually represents. Falls
 * back to the plain version number ("v1", "v3", …) for a model with no batch.
 */
export function versionLabel(model: { id: string; batchId?: string }): string {
  return batchOf(model)?.label ?? `v${versionOrdinal(model.id)}`;
}

/** One object's tile: a display name and its versions, ordered base-first. */
export interface VersionGroup<T> {
  /** Stable key, "<category>/<stem>" — unique per object. */
  key: string;
  /** Display name for the object, with any "(vN)" stripped ("Avenue"). */
  name: string;
  /** Members ordered by version ordinal (v1, v2, v3…); always at least one. */
  members: T[];
}

/** How to read the id, name and bucket (category) off an arbitrary item. */
export interface GroupAccessors<T> {
  id: (item: T) => string;
  name: (item: T) => string;
  /** Items only merge within the same bucket, so unrelated objects that share a
   *  stem across categories never collapse together. */
  category: (item: T) => string;
}

/**
 * Merge items that describe the same object (same category + id stem) into one
 * {@link VersionGroup}. Groups appear in the order their first member is seen
 * (so a pre-sorted `items` yields a pre-sorted group list), and each group's
 * members are sorted base-first by version ordinal.
 */
export function groupByObject<T>(items: T[], get: GroupAccessors<T>): VersionGroup<T>[] {
  const order: string[] = [];
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const key = `${get.category(item)}/${versionStem(get.id(item))}`;
    let bucket = buckets.get(key);
    if (!bucket) { bucket = []; buckets.set(key, bucket); order.push(key); }
    bucket.push(item);
  }
  return order.map((key) => {
    const members = buckets.get(key)!
      .slice()
      .sort((a, b) => versionOrdinal(get.id(a)) - versionOrdinal(get.id(b)));
    return { key, name: baseName(get.name(members[0]!)), members };
  });
}
